import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { message } from "antd";
import "./GooglePayButton.css";

const GooglePayButton = forwardRef(({ 
  validate,
  amount,
  onPaymentSuccess,
  addLog,
  publicKey,
  clientSecret,
  loading,
  setLoading
}, ref) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (!stripe || !clientSecret) return;
    // if(!validate()) {
    //   setIsCheckingAvailability(false);
    //   setCanMakePayment(false);
    //   addLog('Google Pay not available (validation failed)', 'warning');
    //   return;
    // }
    setIsCheckingAvailability(true);
    addLog('Checking Google Pay availability...', 'info');

    const pr = stripe.paymentRequest({
      country: 'GB',
      currency: 'gbp',
      total: {
        label: 'Total',
        amount: Math.round(parseFloat(amount) * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
      supportedPaymentMethods: [{
        supportedMethods: "google_pay",
        data: {
          environment: "TEST",
          apiVersion: 2,
          apiVersionMinor: 0,
          merchantInfo: {
            merchantName: "Your Merchant Name"
          },
          allowedPaymentMethods: [{
            type: "CARD",
            parameters: {
              allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
              allowedCardNetworks: ["MASTERCARD", "VISA"]
            },
            tokenizationSpecification: {
              type: "PAYMENT_GATEWAY",
              parameters: {
                gateway: "stripe",
                "stripe:version": "2025-06-30.basil",
                "stripe:publishableKey": publicKey
              }
            }
          }]
        }
      }]
    });

    pr.canMakePayment().then(result => {
      setIsCheckingAvailability(false);
      setCanMakePayment(!!result);
      
      if (result) {
        setPaymentRequest(pr);
        addLog('Google Pay is available', 'success');
        setLoading(false);
      } else {
        addLog('Google Pay not available', 'warning');
        setLoading(false);
      }
    });

    pr.on('paymentmethod', async (ev) => {
      try {
        messageApi.loading('Processing payment...');
        addLog('Payment initiated via Google Pay', 'info');
        
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: ev.paymentMethod.id
          },
          {
            handleActions: false
          }
        );

        if (error) {
          addLog(`Payment failed: ${error.message}`, 'error');
          ev.complete('fail');
          messageApi.error(error.message);
          return;
        }

        ev.complete('success');
        addLog('Payment succeeded', 'success');
        onPaymentSuccess(ev.paymentMethod.id);
        
      } catch (err) {
        addLog(`Payment error: ${err.message}`, 'error');
        ev.complete('fail');
        messageApi.error('Payment failed');
      }
    });

    return () => {
      if (pr) pr.off('paymentmethod');
    };
  }, [stripe, clientSecret, amount]);

  useImperativeHandle(ref, () => ({
    initiatePayment: () => {
      if (!paymentRequest) {
        messageApi.error('Google Pay not initialized');
        return false;
      }
      if (!canMakePayment) {
        messageApi.error('Google Pay not available in this browser');
        return false;
      }
      paymentRequest.show();
      return true;
    },
    isReady: canMakePayment && !!paymentRequest
  }));

  if (loading || isCheckingAvailability) {

    return (
      <div className="gp-loading-container">
        <div className="gp-loading-card">
          <h3 className="gp-loading-title">Initializing Google Pay</h3>
          <p className="gp-loading-message">Checking availability...</p>
        </div>
        {contextHolder}
      </div>
    );
  }

  if (canMakePayment) {

    return (
      <div className="gp-available-container">
        <div className="gp-available-card">
          <h3 className="gp-available-title">Google Pay Available</h3>
          <p className="gp-available-message">
            You can pay with Google Pay. Click the payment button to continue.
          </p>
        </div>
        {contextHolder}
      </div>
    );
  }

  return (
    <div className="gp-warning-container">
      <div className="gp-warning-card">
        <h3 className="gp-warning-title">Google Pay Not Available</h3>
        <p className="gp-warning-message">
          Your browser doesn't support Google Pay. Please try another payment method.
        </p>
      </div>
      {contextHolder}
    </div>
  );
});

export default GooglePayButton;